import { Col, Divider, FormSection, Row } from '@ksyos/design-system';
import type { ICustomControlProcedureReadProps } from '../../interfaces/';
import { OsasQuestionnaireName } from '../../../../../../common/enums/osasQuestionnaireName';
import { OsasScoreColor } from '../../../../../../common/enums/scoreColor';
import { SomnologyOrganizationType } from '../../../../../../common/enums/somnologyOrganizationType';
import { getEssResultMessage } from '../../../../../../common/utils/getEssResultMessage';
import { getOsasQuestionnaireScore } from '../../../../../../common/utils/osasQuestionnaireUtils';
import { translateKey } from '../../../../../translate';
import { OsasScore } from '../../utils/v1/questionnaires/OsasQuestionnairesRead';
import { OsasoQuestionnaireScoreIndicator } from './OsasvoV1QuestionnaireScoreIndicator';

interface IOsasoQuestionnaire {
    answer: any;
    name: OsasQuestionnaireName;
}

interface IFosq10OsasoQuestionnaire {
    difficultyBeingActiveAtNight: number;
    difficultyBeingActiveInTheMorning: number;
    difficultyConcentratingOnWork: number;
    difficultyDrivingAVehicleLongDistances: number;
    difficultyDrivingAVehicleShortDistances: number;
    difficultyRememberingThings: number;
    difficultyVisitingFriendsAndFamily: number;
    difficultyWatchingVideoOrFilm: number;
    haveRelationshipsDeteriorated: number;
    needForIntimacy: number;
}

interface IEssOsasoQuestionnaire {
    afterLunch: number;
    inAPublicEnvironment: number;
    restingInTheAfternoon: number;
    sittingInACarAsPassenger: number;
    talkingToSomeone: number;
    waitingInTheCarInTraffic: number;
    whenReading: number;
    whenWatchingTv: number;
}

export function OsasvoV1QuestionnairesRead(props: ICustomControlProcedureReadProps) {
    if (
        !props.request.customControlData ||
        props.request.customControlData.somnologyOrganizationType !== SomnologyOrganizationType.ksyosMedicalCenter ||
        !props.procedure.customControlData.questionnaires ||
        !props.procedure.customControlData.questionnaires.some((questionnaire: any) =>
            [OsasQuestionnaireName.fosq10, OsasQuestionnaireName.ess].includes(questionnaire.name)
        )
    ) {
        return null;
    }

    let fosq10Items;
    let essItems;
    let fosq10Score;
    let essScore;

    const essQuestionnaire: IOsasoQuestionnaire = props.procedure.customControlData.questionnaires.find(
        (questionnaire: any) => questionnaire.name === OsasQuestionnaireName.ess
    );
    const fosq10Questionnaire: IOsasoQuestionnaire = props.procedure.customControlData.questionnaires.find(
        (questionnaire: any) => questionnaire.name === OsasQuestionnaireName.fosq10
    );

    if (essQuestionnaire) {
        essItems = getEssItemsOnColumns(essQuestionnaire.answer);
        essScore = getOsasQuestionnaireScore(essQuestionnaire);
    }
    if (fosq10Questionnaire) {
        fosq10Items = getFosq10ItemsOnColumns(fosq10Questionnaire.answer);
        fosq10Score = getOsasQuestionnaireScore(fosq10Questionnaire);
    }

    return (
        <>
            <Divider />

            <FormSection title="FOSQ-10 en ESS: 0-meting">
                {fosq10Questionnaire && (
                    <OsasoQuestionnaireScoreIndicator
                        direction="left"
                        osasoQuestionnaireScoreType="fosq10"
                        score={fosq10Score}
                        variant="gradient"
                    />
                )}

                {essQuestionnaire && (
                    <OsasoQuestionnaireScoreIndicator
                        direction="right"
                        norm={getEssResultMessage(essScore)}
                        osasoQuestionnaireScoreType="ess"
                        score={essScore}
                        variant="edgedGradient"
                    />
                )}
            </FormSection>

            {fosq10Questionnaire && (
                <FormSection title="FOSQ-10">
                    <Row>
                        <Col span={[12, 6]}>{fosq10Items?.leftColumnOfNumberedBadges}</Col>
                        <Col span={[12, 6]}>{fosq10Items?.rightColumnOfNumberedBadges}</Col>
                    </Row>
                </FormSection>
            )}

            {essQuestionnaire && (
                <FormSection title="ESS">
                    <Row>
                        <Col span={[12, 6]}>{essItems?.leftColumnOfNumberedBadges}</Col>
                        <Col span={[12, 6]}>{essItems?.rightColumnOfNumberedBadges}</Col>
                    </Row>
                </FormSection>
            )}
        </>
    );
}

function getFosq10ItemsOnColumns(fosq10Items: IFosq10OsasoQuestionnaire) {
    const leftColumnOfNumberedBadges: JSX.Element[] = [];
    const rightColumnOfNumberedBadges: JSX.Element[] = [];
    const rightColumnOfNumberedBadgesAndNvtValue: JSX.Element[] = [];
    const orderedFosq10Items = Object.entries(fosq10Items).sort((a, b) => a[1] - b[1]);

    for (const [key, value] of orderedFosq10Items) {
        if (value === 1 || value === 2) {
            leftColumnOfNumberedBadges.push(
                <OsasScore
                    color={getColorForFosq10Value(value)}
                    label={translateKey(`osasQuestionnaires.${key}`)}
                    value={value}
                />
            );
        } else if (value === 0) {
            rightColumnOfNumberedBadgesAndNvtValue.push(
                <OsasScore
                    color={getColorForFosq10Value(value)}
                    label={translateKey(`osasQuestionnaires.${key}`)}
                    value="NVT"
                />
            );
        } else {
            rightColumnOfNumberedBadges.push(
                <OsasScore
                    color={getColorForFosq10Value(value)}
                    label={translateKey(`osasQuestionnaires.${key}`)}
                    value={value}
                />
            );
        }
    }

    return {
        leftColumnOfNumberedBadges,
        rightColumnOfNumberedBadges: [...rightColumnOfNumberedBadges, ...rightColumnOfNumberedBadgesAndNvtValue],
    };
}

function getEssItemsOnColumns(essItems: IEssOsasoQuestionnaire) {
    const leftColumnOfNumberedBadges: JSX.Element[] = [];
    const rightColumnOfNumberedBadges: JSX.Element[] = [];
    const orderedEssItems = Object.entries(essItems).sort((a, b) => b[1] - a[1]);

    for (const [key, value] of orderedEssItems) {
        if (value === 2 || value === 3) {
            leftColumnOfNumberedBadges.push(
                <OsasScore
                    color={getColorForEssValue(value)}
                    label={translateKey(`osasQuestionnaires.${key}`)}
                    value={value}
                />
            );
        } else {
            rightColumnOfNumberedBadges.push(
                <OsasScore
                    color={getColorForEssValue(value)}
                    label={translateKey(`osasQuestionnaires.${key}`)}
                    value={value}
                />
            );
        }
    }

    return { leftColumnOfNumberedBadges, rightColumnOfNumberedBadges };
}

function getColorForFosq10Value(value: number) {
    let color;
    switch (value) {
        case 1:
            color = OsasScoreColor.red;
            break;
        case 2:
            color = OsasScoreColor.orange;
            break;
        case 3:
            color = OsasScoreColor.yellow;
            break;
        case 4:
            color = OsasScoreColor.green;
            break;
        default:
            color = OsasScoreColor.gray;
            break;
    }

    return color;
}

function getColorForEssValue(value: number) {
    let color;
    switch (value) {
        case 0:
            color = OsasScoreColor.green;
            break;
        case 1:
            color = OsasScoreColor.yellow;
            break;
        case 2:
            color = OsasScoreColor.orange;
            break;
        case 3:
            color = OsasScoreColor.red;
            break;
        default:
            break;
    }

    return color;
}

export interface IOsasoScoreProps {
    color?: string;
    label: string;
    value: number | string;
}
